import { Tokens } from "commons/interface";
import { WalletResponse } from "data/slices/wallet/interface";
import { format } from "date-fns";
import { Dayjs } from "dayjs";
import { isEmpty, isEqual, isUndefined } from "lodash";

import {
  DEFAULT_PLACEAHOLDER,
  DEFAULT_TERMS_CHARS_TO_SHOW,
  PROFILE_DOB_MIN_AGE,
  STATUS_TEST,
  STATUS_TYPE,
  STORAGE_IS_FIRST_LOGIN,
  STORAGE_IS_INITIAL_SURVEY,
  STORAGE_IS_MATCH_POPUP_SHOWN,
  STORAGE_IS_POPUP_SHOWN,
  STORAGE_IS_SURVEY_BLOCK,
  STORAGE_REFRESH_TOKEN,
  STORAGE_TOKEN,
} from "./constants";

export const saveToLocalStorage = (key: string, value: string) => {
  localStorage.setItem(key, value);
};

export const getFromLocalStorage = (key: string) => {
  return localStorage.getItem(key);
};

export const isPopupShown = () => {
  return localStorage.getItem(STORAGE_IS_POPUP_SHOWN) === "true";
};

export const setPopupShown = () => {
  localStorage.setItem(STORAGE_IS_POPUP_SHOWN, "true");
};

export const isMatchPopupShown = () => {
  return localStorage.getItem(STORAGE_IS_MATCH_POPUP_SHOWN) === "true";
};

export const setMatchPopupShown = () => {
  localStorage.setItem(STORAGE_IS_MATCH_POPUP_SHOWN, "true");
};

export const saveTokens = (tokens: Tokens) => {
  saveToLocalStorage(STORAGE_TOKEN, tokens.token);
  saveToLocalStorage(STORAGE_REFRESH_TOKEN, tokens.refreshToken);
  saveToLocalStorage(STORAGE_IS_FIRST_LOGIN, String(tokens.isFirstLogin));
  saveToLocalStorage(STORAGE_IS_INITIAL_SURVEY, String(tokens.isInitialSurvey));
  saveToLocalStorage(STORAGE_IS_SURVEY_BLOCK, String(!!tokens.isSurveyBlock));
  saveToLocalStorage(STORAGE_IS_POPUP_SHOWN, "false");
  saveToLocalStorage(STORAGE_IS_MATCH_POPUP_SHOWN, "false");
};

export const getTokens = (): Tokens => {
  // Get the raw values from localStorage
  const firstLoginValue =
    getFromLocalStorage(STORAGE_IS_FIRST_LOGIN) === "true";
  const initialSurveyValue =
    getFromLocalStorage(STORAGE_IS_INITIAL_SURVEY) === "true";

  return {
    token: getFromLocalStorage(STORAGE_TOKEN),
    refreshToken: getFromLocalStorage(STORAGE_REFRESH_TOKEN),
    isFirstLogin: firstLoginValue,
    isInitialSurvey: initialSurveyValue,
  };
};

export const logout = () => {
  localStorage.clear();
};

export const hasLoginTokens = (): boolean => {
  const tokens = getTokens();
  return tokens.refreshToken !== null && tokens.token !== null;
};

export const formatDate = (inputDate: string, toFormat: string) => {
  if (isUndefined(inputDate) || isEmpty(inputDate)) {
    return DEFAULT_PLACEAHOLDER;
  }

  var inDate = inputDate;
  if (inDate && inDate.includes("T")) {
    inDate = inDate.replace("T", " ");
  }

  const date = new Date(inDate);
  return format(date, toFormat);
};

export const getDateFormat = (date?: Dayjs | null) => {
  if (!date || date === null) {
    return "";
  }

  return date.format("DD MMM YYYY");
  //return `${date.month() + 1}/${date.day()}/${date.year()}`;
};

export const getStringWithDefaultValue = (input?: string) => {
  if (isUndefined(input) || isEmpty(input)) {
    return DEFAULT_PLACEAHOLDER;
  }
  return input;
};

export const getSurveyURLToOpen = (url: string, guid: string, key: string) => {
  //stage.members.precisionsample.com/survey/entry.aspx?mid=[%MID%]&project=1010000002&key=%key%
  var updatedUrl = url.replace("[%MID%]", guid);
  updatedUrl = updatedUrl.replace("%%key%%", key);
  return updatedUrl;
};

export const getCouponStatusType = (status: string) => {
  if (!status) {
    return undefined;
  }

  var ipstatus = status.toLowerCase();
  if (
    isEqual(ipstatus, STATUS_TEST.SUCCESS.toLowerCase()) ||
    isEqual(ipstatus, STATUS_TEST.RESOLVED.toLowerCase())
  ) {
    return STATUS_TYPE.SUCCESS;
  } else if (isEqual(ipstatus, STATUS_TEST.PENDING.toLowerCase())) {
    return STATUS_TYPE.PENDING;
  } else if (isEqual(ipstatus, STATUS_TEST.FAILED.toLowerCase())) {
    return STATUS_TYPE.FAILED;
  } else if (isEqual(ipstatus, STATUS_TEST.OPEN.toLowerCase())) {
    return STATUS_TYPE.OPEN;
  } else {
    return undefined;
  }
};

export const walletBalance = (walletdetails: WalletResponse) => {
  return walletdetails
    ? parseFloat((walletdetails.earnings - walletdetails.redeemed).toFixed(2))
    : 0;
};

export const appendPoints = (points: number) => {
  return `${points}pts`;
};

export const formatConversion = (conversion?: number) => {
  if (isUndefined(conversion) || conversion === null) {
    return DEFAULT_PLACEAHOLDER;
  } else if (conversion === 0) {
    return "0";
  }
  // Convert to percentage and round to 2 decimal places
  const percentage = (conversion * 100).toFixed(2);
  return `${percentage}`;
};

export const formatScore = (score?: number) => {
  if (isUndefined(score) || score === null) {
    return DEFAULT_PLACEAHOLDER;
  } else if (score === 0) {
    return "0";
  }
  // Round to 2 decimal places
  return score.toFixed(2);
};

//will return date in yyyy-MM-dd format and also the day
export const getDateInFormat = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const monthName = date.toLocaleString("default", { month: "long" });
  return {
    formatted: `${year}-${month}-${day}`,
    day: day,
    tooltip: `${monthName} ${day}`,
    month: monthName,
  };
};

export const decrementOneDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() - 1);
  return newDate;
};

export const isStringEmpty = (input: string) => {
  return isUndefined(input) || isEmpty(input);
};

export const getUserName = (firstName: string, lastName: string) => {
  if (!firstName && !lastName) {
    return DEFAULT_PLACEAHOLDER;
  }

  return `${firstName} ${lastName?.charAt(0) ?? ""}`;
};

export const isValidAge = (selectedDate: Date) => {
  const minDate = new Date(new Date());
  minDate.setFullYear(minDate.getFullYear() - PROFILE_DOB_MIN_AGE);
  return selectedDate <= minDate;
};

export const termsTextToShow = (terms: string, showFull: boolean) => {
  if (showFull) {
    return terms;
  }

  return terms.length <= DEFAULT_TERMS_CHARS_TO_SHOW
    ? terms
    : terms.substring(0, DEFAULT_TERMS_CHARS_TO_SHOW);
};
